/**
 * In welcher Phase steckt diese Reise — Entwurf, bald, unterwegs, Nachlese?
 *
 * ## Warum es das im Planer bisher nicht gab
 *
 * Der Planer kannte nur eine Frage zur Zeit: „ist die Reise abgehakt?"
 * (`reiseabschluss.ts`). Alles andere ergab sich aus dem Kalender im Kopf des
 * Betrachters. Das reicht nicht mehr, seit die Oberfläche sich nach der Lage
 * richten soll: Am Entwurfstag will man Nächte und Quartiere sehen, unterwegs
 * den Haken „erreicht" und die Buchung, hinterher die Notiz. Diese Vorgabe
 * braucht **eine** Regel — sonst entscheidet jedes Panel für sich, und zwei
 * Flächen nebeneinander behaupten Verschiedenes über denselben Tag.
 *
 * ## Vier Phasen, und warum gerade diese vier
 *
 * - `entwurf` — kein Beginn eingetragen oder er liegt mehr als
 *   {@link BALD_TAGE} Tage voraus. Hier wird gebaut: Stationen, Dauern, Route.
 * - `bald` — der Beginn liegt in Reichweite. Jetzt zählt, was noch offen ist
 *   (Buchungen, Belege), nicht mehr die Grobplanung.
 * - `unterwegs` — vom Starttag bis einen Tag nach dem Ende. Jetzt zählt der
 *   Zustand: erreicht, gebucht, wo schlafe ich heute.
 * - `nachlese` — danach, oder sobald die Reise abgehakt ist. Jetzt zählen
 *   Notizen und Belege; was gebucht war, ist Geschichte.
 *
 * ## Der eine Tag Nachlauf ist geerbt, nicht erfunden
 *
 * `beginn ≤ heute ≤ ende + 1 Tag` ist wortgleich die Regel des Servers in
 * `oeffentlich._laeuft_noch` (dort mit derselben Begründung: Wer am
 * Rückreisetag abends ankommt, ist bis dahin unterwegs). Die **Regel** ist
 * dieselbe, die **Quelle** ist es bewusst nicht: Die geteilte Seite bekommt
 * `laeuft` fertig vom Server und rechnet nichts nach (siehe
 * `kapitelklappen.istUnterwegs`), der Planer hat keinen solchen Boten — er
 * hält die Reise selbst in der Hand, samt ungespeicherter Änderung am
 * Startdatum. Eine Serverfrage je Tastendruck wäre der falsche Preis für eine
 * Auskunft, die aus zwei Feldern folgt.
 *
 * Eine abgehakte Reise ist immer `nachlese`, auch wenn der Kalender noch
 * läuft — dieselbe Paarung wie beim Standortschutz des Servers und wie in
 * `kapitelklappen.istUnterwegs`.
 *
 * **Reine Funktionen** — kein DOM, kein Netz, **keine Uhr**. `heute` kommt
 * herein. Das ist nicht nur der Prüfbarkeit wegen so: Eine Funktion, die
 * `new Date()` selbst ruft, ist um Mitternacht eine andere als eine Minute
 * davor, und niemand sieht es ihr an. Wer sie ruft, entscheidet, wann „heute"
 * festgestellt wird — im Planer einmal beim Laden.
 */
import { istAbgeschlossen, type MitAbschluss } from './reiseabschluss';

/** Die vier Lagen. Mehr sind nicht vorgesehen. */
export type Reisephase = 'entwurf' | 'bald' | 'unterwegs' | 'nachlese';

/** Alle vier in ihrer natürlichen Reihenfolge — für Tests und Schleifen. */
export const REISEPHASEN: readonly Reisephase[] = [
  'entwurf',
  'bald',
  'unterwegs',
  'nachlese'
] as const;

/**
 * Ab wann eine Reise „bald" ist: vierzehn Tage.
 *
 * Zwei Wochen sind die Frist, in der das Buchen anfängt und das Umplanen
 * aufhört — kürzer wäre die Phase nutzlos (drei Tage vorher ist alles fest),
 * länger wäre sie keine: Ein Drittel des Jahres „bald" zu nennen sagt nichts.
 * Die Zahl steht hier als Konstante, damit die Tests sie nennen können statt
 * sie nachzurechnen.
 */
export const BALD_TAGE = 14;

/** `2026-08-03T18:30:00` → `[2026, 8, 3]`, sonst `null`. Wie in `datum.ts`. */
function teile(iso: string | null | undefined): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/**
 * Ein Kalendertag als Tageszahl — `null`, wenn nichts Brauchbares dasteht.
 *
 * Über `Date.UTC` aus den **zerlegten** Teilen und nicht über `new Date(iso)`:
 * Die Zeichenkette `2026-08-03` läse jede Zeitzonenrechnung als
 * UTC-Mitternacht, und westlich von Greenwich wäre der Reisebeginn dann der
 * 2. August. Beide Seiten des Vergleichs entstehen hier auf demselben Weg,
 * also fällt die Zeitzone aus der Differenz heraus.
 */
function tageszahl(iso: string | null | undefined): number | null {
  const t = teile(iso);
  if (!t) return null;
  return Date.UTC(t[0], t[1] - 1, t[2]) / 86_400_000;
}

/**
 * Der heutige Kalendertag als `YYYY-MM-DD` — in **Ortszeit**.
 *
 * Der Aufrufer bringt die Uhr mit; hier wird nur zerlegt. `toISOString()`
 * wäre die kürzere Fassung und die falsche: Sie rechnet nach UTC um, und um
 * 23:30 mitteleuropäischer Sommerzeit steht dort schon der nächste Tag.
 */
export function alsTag(jetzt: Date): string {
  const m = String(jetzt.getMonth() + 1).padStart(2, '0');
  const d = String(jetzt.getDate()).padStart(2, '0');
  return `${jetzt.getFullYear()}-${m}-${d}`;
}

/** Was von einer Reise gebraucht wird — absichtlich nicht `Trip`. */
export interface Phasenreise extends MitAbschluss {
  begin_date?: string | null;
  end_date?: string | null;
}

/**
 * Wie viele Tage liegen zwischen dem Beginn und heute? `null`: unbekannt.
 *
 * Positiv heißt „liegt vor uns", negativ „liegt hinter uns", `0` heißt heute.
 * Die Zahl steht als eigene Auskunft zur Verfügung, weil die Oberfläche sie
 * nennen können soll („in 9 Tagen") — die Phase allein sagte dafür zu wenig.
 */
export function tageBisBeginn(
  reise: Phasenreise | null | undefined,
  heute: string
): number | null {
  const beginn = tageszahl(reise?.begin_date);
  const jetzt = tageszahl(heute);
  if (beginn == null || jetzt == null) return null;
  return beginn - jetzt;
}

/**
 * Die Phase dieser Reise am Tag `heute` (`YYYY-MM-DD`).
 *
 * Die Reihenfolge der Prüfungen ist die Aussage:
 *
 * 1. **Abgehakt schlägt alles.** Wer abends heimkommt und die Reise abhakt,
 *    ist fertig, obwohl der Nachlauftag noch gilt.
 * 2. **Ohne Beginn ist es ein Entwurf.** Und zwar auch dann, wenn ein Ende
 *    dasteht: Eine Reise ohne Starttag hat keinen Kalenderbezug, aus dem sich
 *    etwas anderes ableiten ließe. (Der Server sagt in derselben Lage
 *    `_laeuft_noch` — er beantwortet aber auch eine andere Frage: „darf ich
 *    den Standort zeigen", und dort ist Großzügigkeit die stille Richtung.
 *    Hier wäre sie eine Oberfläche, die für einen Rohentwurf den
 *    Erreicht-Haken in den Kopf setzt.)
 * 3. **Ohne Ende gilt der Beginn als Ende.** Eine Tagesreise ohne Enddatum ist
 *    an ihrem Tag unterwegs und einen Tag später vorbei — nicht für immer
 *    unterwegs.
 * 4. Danach die drei Kalenderfälle in der Reihenfolge, in der sie eintreten.
 *
 * Ein unlesbares `heute` ergibt `entwurf`: Ohne Bezugstag gibt es keine
 * Kalenderaussage, und `entwurf` ist die Phase, die nichts behauptet.
 */
export function reisephase(
  reise: Phasenreise | null | undefined,
  heute: string
): Reisephase {
  if (istAbgeschlossen(reise)) return 'nachlese';

  const jetzt = tageszahl(heute);
  const beginn = tageszahl(reise?.begin_date);
  if (jetzt == null || beginn == null) return 'entwurf';

  // Ohne Ende dauert die Reise einen Tag — siehe oben.
  const ende = tageszahl(reise?.end_date) ?? beginn;
  // Der geerbte Nachlauftag aus `oeffentlich._laeuft_noch`.
  if (jetzt > ende + 1) return 'nachlese';
  if (jetzt >= beginn) return 'unterwegs';
  return beginn - jetzt <= BALD_TAGE ? 'bald' : 'entwurf';
}

/**
 * Ist die Reise gerade unterwegs?
 *
 * Nur die Abkürzung für `reisephase(...) === 'unterwegs'`, damit an den
 * Aufrufstellen kein Zeichenkettenvergleich steht. Ein Tippfehler in
 * `'unterwegs'` wäre sonst eine Bedingung, die immer falsch ist und nie
 * auffällt.
 */
export function istUnterwegsPhase(phase: Reisephase): boolean {
  return phase === 'unterwegs';
}
